"use client";

import { useEffect, useState } from "react";
import { Loader2, Infinity as InfinityIcon, ShieldCheck, ArrowRightLeft, PhoneCall, PhoneIncoming } from "lucide-react";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { useDialCreditBalance } from "@/app/(authenticated)/settings/_modules/hooks/useDialCredits";
import {
  direct130CanDial,
  direct130Minutes,
  direct130RateLabel,
  type Direct130Dialing,
} from "@/app/(authenticated)/settings/_modules/services/dialCreditsService";
import { useDirect130Host } from "@/app/_module/utils/direct130HostContext";
import { MentorMark } from "@/app/_module/components/MentorMark";
import { insuraMentorUnlockedFor } from "@/app/_module/utils/featureFlags";
// Same mark the sidebar uses for AI Calling — one icon per feature, everywhere.
import { InsuraBotIcon } from "@/app/_module/components/icons/InsuraBotIcon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";

const STORAGE_KEY = "dial-credits-indicator-display-mode";
type DisplayMode = "minutes" | "dollars";

/**
 * "Talk Token" — the dial-minutes mark: a handset with a clock badge (owner 2026-11-01; the
 * coin-and-waveform before it read as a sparkle). The badge sits in the handset's empty
 * top-right corner, so there is no knockout fill and it holds on dark backgrounds. Checked
 * at 16 px, where the handset silhouette and the badge still read.
 *
 * Uses currentColor throughout, so the pill's existing healthy / low / empty colour
 * states drive it with no extra wiring.
 */
export function TalkToken({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <g transform="translate(-1 3) scale(0.78)">
        <path
          d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
          strokeWidth="2.4"
        />
      </g>
      <circle cx="17.8" cy="6.2" r="4.6" />
      <path d="M17.8 3.9v2.4l1.6 1.1" />
    </svg>
  );
}

/**
 * Compact balance widget that lives in the navbar.
 *
 * Visible to EVERY user (2026-07-25). Buying is a separate thing and stays
 * super_agent-only: the Settings → Dial Minutes tab is still gated in
 * settings/page.tsx, and so is `DialCreditsMenuItem` below, since that one
 * navigates INTO that tab. This pill is read-only — it shows a balance and
 * toggles its own units, nothing more.
 *
 * Click to TOGGLE between minutes remaining and $ balance.
 * Choice is persisted to localStorage so it sticks across page loads.
 *
 * Color-coded by balance health:
 *   - Empty (red)         → balance = $0
 *   - Low (amber)         → balance < user's threshold
 *   - Healthy (slate)     → otherwise
 */
export default function DialCreditsIndicator() {
  const { user } = useUserContext();

  // Only affects the copy: super agents get pointed at the Settings tab they
  // actually have; everyone else is just told what the click does.
  const accountType = (user as any)?.user?.accountType ?? (user as any)?.accountType;
  const isSuperAgent = accountType === "super_agent";

  const balanceQuery = useDialCreditBalance();

  // Display mode (minutes ↔ dollars), persisted to localStorage so it sticks
  const [displayMode, setDisplayMode] = useState<DisplayMode>("minutes");

  // Counts clicks, not mode. Two reasons: it drives the animation `key` so a flip
  // replays even if you somehow land on the same mode, and starting at 0 means the
  // coin doesn't burst on page load — only on a real interaction.
  //
  // MUST stay above the loading early-return below: declaring it after that return
  // makes it a conditional hook, and React errors with "Rendered more hooks than
  // during the previous render" the moment the skeleton swaps for the real pill.
  const [flips, setFlips] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved === "minutes" || saved === "dollars") {
        setDisplayMode(saved);
      }
    } catch {
      /* localStorage unavailable */
    }
  }, []);

  // Initial load skeleton — show a tiny placeholder so layout doesn't shift
  if (balanceQuery.isLoading && !balanceQuery.data) {
    return (
      <button
        type="button"
        className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm border rounded-md bg-background text-slate-400"
        disabled
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading…</span>
      </button>
    );
  }

  const data = balanceQuery.data;
  const balanceCents = data?.balanceCents ?? 0;
  // Direct 130 agents: this wallet pays for their dialing (until they clear the Quote & Enroll bar), at its own rate.
  const dialing = data?.direct130Dialing ?? null;
  const freeDialing = !!dialing && !dialing.metered;
  const minutes = dialing ? direct130Minutes(balanceCents, dialing) : data?.estimatedMinutesRemaining ?? 0;
  const isEmpty = balanceCents === 0;
  const isLow = data?.isLowBalance ?? false;
  // A free Direct 130 agent at $0 isn't blocked from anything that matters: no red pill.
  const alarm = isEmpty && !freeDialing;

  const toggleDisplayMode = () => {
    setFlips((n) => n + 1);
    setDisplayMode((prev) => {
      const next = prev === "minutes" ? "dollars" : "minutes";
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(STORAGE_KEY, next);
        }
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Thousands separators on BOTH units. Minutes already used toLocaleString, but
  // dollars was a raw toFixed(2) — so a healthy balance rendered "$1586.43" next to
  // "1,586 min". Currency formatting keeps them consistent at any size.
  const dollarValue = (balanceCents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });

  // Format what's displayed in the pill based on current mode
  const pillValue =
    displayMode === "dollars" ? dollarValue : freeDialing ? "Free dialing" : `${minutes.toLocaleString()} min`;

  // Visual variant — matches agency box (bordered rounded-md), color-coded by health
  let variantClasses = "border bg-background text-foreground hover:bg-muted/50";
  let iconClasses = "text-primary";
  if (alarm) {
    variantClasses =
      "border-red-200 bg-red-50 text-red-700 hover:bg-red-100";
    iconClasses = "text-red-600";
  } else if (isLow) {
    variantClasses =
      "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100";
    iconClasses = "text-amber-600";
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Wallet — ${pillValue}. Open balance breakdown.`}
          data-tour="navbar-dial-credits"
          className={`hidden md:flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors active:scale-[0.98] cursor-pointer ${variantClasses}`}
        >
          {/* Coin burst used to live here, but the pill's click now OPENS the panel —
              it no longer flips anything, so a burst here would fire on every open and
              collide with the panel that drops directly below it. It moved next to the
              Show $/min toggle inside the panel, which is what actually flips now. */}
          <span className="tabular-nums inline-block min-w-[62px] text-center">
            {pillValue}
          </span>
        </button>
      </PopoverTrigger>

      {/* Breakdown panel.
          WHY THIS EXISTS (2026-07-28): the pill showed one number and went RED at
          zero, so agents read "0 min" as "I can't dial" and stopped working. In
          reality this wallet funds ONLY AI Outbound and DNC Scrub — manual, power
          and multi-line dialing are not metered by it at all (verified: the
          communication controller and multi-line services never touch
          DialCreditService). Spelling that out is the entire point of this panel. */}
      <PopoverContent align="end" side="bottom" className="w-[288px] p-0 overflow-hidden">
        {/* `relative` + perspective so the coin burst below is positioned against
            THIS header and reads as 3D rather than a flat squash. */}
        <div
          className="relative px-3.5 pt-3 pb-2.5 border-b bg-muted/30"
          style={{ perspective: "480px" }}
        >
          {/* The coin now bursts here, from the control that actually flips the units.
              Anchored under the toggle and pointer-events-none so it can never swallow
              a second click; rendered only after a real flip (flips>0) so it stays
              quiet when the panel first opens. */}
          {flips > 0 && (
            <span
              key={`coin-${flips}`}
              aria-hidden="true"
              className="ic-coin-burst pointer-events-none absolute right-5 top-6 z-50"
            >
              <TalkToken className={`h-9 w-9 ${iconClasses}`} />
            </span>
          )}

          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Wallet balance
            </span>
            <button
              type="button"
              onClick={toggleDisplayMode}
              className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowRightLeft className="h-2.5 w-2.5" />
              {displayMode === "minutes" ? "Show $" : "Show min"}
            </button>
          </div>
          {/* Keyed on the flip count so React remounts it and the CSS animation
              replays: the value swings in big, then settles. */}
          <p
            key={`value-${flips}`}
            className={`mt-0.5 text-xl font-semibold tabular-nums ${flips > 0 ? "ic-value-flip" : ""} ${alarm ? "text-red-600" : isLow ? "text-amber-600" : "text-foreground"}`}
          >
            {pillValue}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {displayMode === "minutes" ? dollarValue : `${minutes.toLocaleString()} min`} available
          </p>
        </div>

        {dialing ? (
          <Direct130Breakdown dialing={dialing} minutes={minutes} isEmpty={isEmpty} />
        ) : (
        <>
        {/* Two groups on purpose. The confusion this panel exists to kill is
            "one number = everything is blocked", so the split has to be explicit:
            these two spend the balance, everything below is already paid for. */}
        <div className="px-3.5 py-2.5 space-y-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Uses this balance
          </p>
          <Line
            icon={<InsuraBotIcon size={14} />}
            label="AI Calling"
            value={isEmpty ? "Needs credits" : `${minutes.toLocaleString()} min`}
            tone={isEmpty ? "bad" : "normal"}
          />
          <Line
            icon={<ShieldCheck className="h-3.5 w-3.5" />}
            label="DNC Scrub"
            value={isEmpty ? "Needs credits" : "Per list"}
            tone={isEmpty ? "bad" : "normal"}
          />
        </div>

        <div className="border-t px-3.5 py-2.5 space-y-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
            Included with your plan
          </p>
          <Line
            icon={<PhoneCall className="h-3.5 w-3.5" />}
            label="Manual, Power & Multi-line"
            value="Unlimited"
            tone="good"
          />
          {/* InsuraMentor is "Included with all subscriptions" per AppSidebar and has
              NO billing wired anywhere — so it belongs here, never in the metered
              group. Mirrors the sidebar's own insuraMentorUnlockedFor gate so this
              row can't claim it's live before the feature ships. */}
          <Line
            icon={<MentorMark className="h-3.5 w-3.5" />}
            label="InsuraMentor"
            value={insuraMentorUnlockedFor(user?.user) ? "Included" : "Coming soon"}
            tone={insuraMentorUnlockedFor(user?.user) ? "good" : "muted"}
          />
        </div>

        {/* The reassurance. Deliberately the only coloured block, so it lands at a
            glance even on someone who opened this panicking about a red 0. */}
        <div className="mx-3.5 mb-3 mt-2.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5">
          <div className="flex items-start gap-2">
            <InfinityIcon className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-emerald-900 leading-tight">
                You can always dial
              </p>
              <p className="text-[11px] text-emerald-800/90 leading-snug mt-0.5">
                Manual, Power and Multi-line calling never use this balance — they keep
                working at $0.
              </p>
            </div>
          </div>
        </div>

        </>
        )}

        {(isSuperAgent || dialing) && (
          <div className="border-t px-3.5 py-2 bg-muted/20">
            <p className="text-[11px] text-muted-foreground">
              Top up in <span className="font-medium text-foreground">Settings → Dial Minutes</span>
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Direct 130: dialing draws on this wallet until the agent's carrier-approved Quote & Enroll issued premium over the
 * last 30 days clears the bar; then it's free. AI Calling and InsuraMentor are off on that address, so no rows.
 */
function Direct130Breakdown({ dialing, minutes, isEmpty }: { dialing: Direct130Dialing; minutes: number; isEmpty: boolean }) {
  const money = (n: number) => n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const canDial = direct130CanDial(dialing);
  return (
    <>
      <div className="px-3.5 py-2.5 space-y-2.5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Uses this balance
        </p>
        {dialing.metered && (
          <Line
            icon={<PhoneCall className="h-3.5 w-3.5" />}
            label="Manual, Power & Multi-line"
            value={canDial ? `${minutes.toLocaleString()} min · ${direct130RateLabel(dialing)}` : "Needs credits"}
            tone={canDial ? "normal" : "bad"}
          />
        )}
        {dialing.metered && dialing.inboundMillsPerMinute > 0 && (
          <Line
            icon={<PhoneIncoming className="h-3.5 w-3.5" />}
            label="Calls in"
            value={`$${(dialing.inboundMillsPerMinute / 1000).toFixed(4)}/min`}
            tone="normal"
          />
        )}
        {/* No DNC Scrub row here for now (owner 2026-11-01). */}
      </div>

      {!dialing.metered && (
        <div className="border-t px-3.5 py-2.5 space-y-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Free for you</p>
          <Line icon={<PhoneCall className="h-3.5 w-3.5" />} label="Manual, Power & Multi-line" value="Free" tone="good" />
        </div>
      )}

      <div
        className={`mx-3.5 mb-3 mt-2.5 rounded-md border px-3 py-2.5 ${
          dialing.metered ? "bg-muted/30" : "border-emerald-200 bg-emerald-50"
        }`}
      >
        <p className={`text-xs font-semibold leading-tight ${dialing.metered ? "text-foreground" : "text-emerald-900"}`}>
          {dialing.metered ? `Dialing is free at ${money(dialing.threshold)}` : "Dialing is free"}
        </p>
        <p className={`text-[11px] leading-snug mt-0.5 ${dialing.metered ? "text-muted-foreground" : "text-emerald-800/90"}`}>
          You have {money(dialing.issuedPremium30d)} issued through Quote &amp; Enroll in the last 30 days.
        </p>
      </div>
    </>
  );
}

/** One row of the "what this pays for" list. */
function Line({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: "normal" | "bad" | "good" | "muted";
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="inline-flex items-center gap-2 text-xs text-foreground">
        <span className="text-muted-foreground">{icon}</span>
        {label}
      </span>
      <span
        className={`text-[11px] tabular-nums ${
          tone === "bad"
            ? "text-red-600"
            : tone === "good"
              ? "text-emerald-700 font-medium"
              : tone === "muted"
                ? "text-muted-foreground/70 italic"
                : "text-muted-foreground"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Dropdown menu row showing AI calling credits balance + minutes.
 * Used inside the user-avatar dropdown (DashboardHeader). Auto-hides for non-super_agent.
 *
 * Click → navigate to Settings → AI Credits tab. Caller passes onNavigate so this stays
 * decoupled from any specific router instance.
 */
export function DialCreditsMenuItem({ onNavigate }: { onNavigate: () => void }) {
  const { user } = useUserContext();
  const accountType = (user as any)?.user?.accountType ?? (user as any)?.accountType;
  const isSuperAgent = accountType === "super_agent";

  // Direct 130 agents top up here too: their dialing runs on this wallet.
  const onDirect130 = useDirect130Host();
  const balanceQuery = useDialCreditBalance({ enabled: isSuperAgent || onDirect130 });

  if (!isSuperAgent && !balanceQuery.data?.direct130Dialing) return null;

  const data = balanceQuery.data;
  const balanceCents = data?.balanceCents ?? 0;
  const dialing = data?.direct130Dialing ?? null;
  const minutes = dialing ? direct130Minutes(balanceCents, dialing) : data?.estimatedMinutesRemaining ?? 0;
  const isEmpty = balanceCents === 0;
  const isLow = data?.isLowBalance ?? false;
  const isLoading = balanceQuery.isLoading && !data;

  let valueColor = "text-foreground";
  let iconColor = "text-primary";
  if (isEmpty) {
    valueColor = "text-red-600";
    iconColor = "text-red-600";
  } else if (isLow) {
    valueColor = "text-amber-600";
    iconColor = "text-amber-600";
  }

  return (
    <div
      role="menuitem"
      tabIndex={-1}
      onClick={onNavigate}
      className="relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground cursor-pointer"
    >
      <TalkToken className={`h-4 w-4 mr-2 ${iconColor}`} />
      {isLoading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      ) : (
        <span className={`tabular-nums ${valueColor}`}>
          {(balanceCents / 100).toLocaleString(undefined, { style: "currency", currency: "USD" })} · {minutes.toLocaleString()}m
        </span>
      )}
    </div>
  );
}
